/**
 * Allotment of Shares — Otherwise Than Cash (M32202) mapper.
 * 공유 상태(OtcState) → [[변수]] payload. CDD(cddMapper) 미러링.
 *
 * OTC 는 12문서가 값을 공유하므로, 병합 payload 하나를 만들어 모든 템플릿에 전달한다
 * (템플릿은 자기가 쓰는 [[변수]] 만 치환, 나머지는 무시 — 백엔드 documentVariableReplacer 동작).
 *
 * 반복부(실제 템플릿):
 *   [[if allottees]] … allottee_name / allottee_address / allottee_shares (Statement 는 allottee_shares_otherwise,
 *                       Section78 는 allottee_no/allottee_id_no/allottee_share_type/allottee_share_count) [[/if allottees]]
 *   [[if shares]]    … share_type / share_details / share_number / share_price_per_share / share_total_issued [[/if shares]] (Section78)
 * 조건([[if X]]) 은 boolean flag 로 출력.
 */
import { OTC_ALLOTTEE_MAX, OTC_SHARE_CLASS_MAX } from '../components/otc/otcFields';

export interface OtcState {
  values: Record<string, string>;    // 스칼라 + flat 반복행 키(allottee_name_${n} 등)
  flags: Record<string, boolean>;    // 조건 토글
  choices: Record<string, string>;   // resolution_basis / statement_resolution_basis
  allotteeCount: number;
  shareClassCount: number;
  subscriberType: 'individual' | 'corporate';
  /** [[Signers]] — 문서(서명영역) varKey 별 선택된 서명자. prepare-documents 의 signers 로 전달. */
  signers: Record<string, any[]>;
}

export function emptyOtcState(): OtcState {
  return {
    values: {},
    flags: { has_constitution: true },
    choices: { resolution_basis: 'constitution', statement_resolution_basis: 'dividend' },
    allotteeCount: 2,
    shareClassCount: 1,
    subscriberType: 'individual',
    signers: {},
  };
}

const num = (v: any) => String(v ?? '').trim();

/** flat allottee_*_${n} → [[if allottees]] 배열. 빈 행은 스킵, allottee_no 자동 부여. */
export function buildAllottees(state: OtcState): Record<string, string>[] {
  const v = state.values || {};
  const max = Math.min(Math.max(1, state.allotteeCount || 1), OTC_ALLOTTEE_MAX);
  const rows: Record<string, string>[] = [];
  let no = 0;
  for (let n = 1; n <= max; n++) {
    const name = num(v[`allottee_name_${n}`]);
    const address = num(v[`allottee_address_${n}`]);
    const shares = num(v[`allottee_shares_${n}`]);
    const idNo = num(v[`allottee_id_no_${n}`]);
    const shareType = num(v[`allottee_share_type_${n}`]) || 'Ordinary';
    if (!name && !address && !shares && !idNo) continue;
    no += 1;
    rows.push({
      allottee_no: String(no),
      allottee_name: name,
      allottee_address: address,
      allottee_id_no: idNo,
      // 주식수 — 문서마다 다른 변수명으로도 접근 가능하게 alias
      allottee_shares: shares,
      allottee_shares_otherwise: shares,
      allottee_share_count: shares,
      allottee_share_type: shareType,
    });
  }
  return rows;
}

/** flat share_class_*_${n} → [[if shares]] 배열 (Section 78). */
export function buildShareClasses(state: OtcState): Record<string, string>[] {
  const v = state.values || {};
  const max = Math.min(Math.max(1, state.shareClassCount || 1), OTC_SHARE_CLASS_MAX);
  const rows: Record<string, string>[] = [];
  for (let n = 1; n <= max; n++) {
    const type = num(v[`share_class_type_${n}`]);
    const details = num(v[`share_class_details_${n}`]);
    const number = num(v[`share_class_number_${n}`]);
    const price = num(v[`share_class_price_${n}`]);
    const total = num(v[`share_class_total_${n}`]);
    if (!type && !details && !number && !price && !total) continue;
    rows.push({
      share_type: type,
      share_details: details,
      share_number: number,
      share_price_per_share: price,
      share_total_issued: total,
    });
  }
  return rows;
}

/** 조건 토글 → 실제 템플릿 [[if X]] 불리언 (complement 포함). */
export function expandFlags(state: OtcState): Record<string, boolean> {
  const f = state.flags || {};
  const c = state.choices || {};
  const basis = c.resolution_basis || 'constitution';
  const stBasis = c.statement_resolution_basis || 'dividend';
  return {
    has_constitution: f.has_constitution !== false,
    multiple_members: !!f.is_multiple_member, // Waiver docx 변수 — is_multiple_member 로 통일(주주 수 자동계산)
    is_multiple_member: !!f.is_multiple_member,
    is_sole_member: !f.is_multiple_member,
    is_multiple_director_company: !!f.is_multiple_director_company,
    is_sole_director_company: !f.is_multiple_director_company,
    grant_of_rights: !!f.grant_of_rights,
    has_conditions: !!f.has_conditions,
    is_politician: !!f.is_politician,
    is_pep_connected: !!f.is_pep_connected,
    holds_govt_position: !!f.holds_govt_position,
    is_trustee: !!f.is_trustee,
    clause_2_applicable: !!f.clause_2_applicable,
    clause_3_applicable: !!f.clause_3_applicable,
    // 결의 근거 (DCR)
    resolution_basis_constitution: basis === 'constitution',
    resolution_basis_articles: basis === 'articles',
    resolution_basis_third_schedule: basis === 'third_schedule',
    // Statement clause 3 결의 근거
    resolution_basis_dividend: stBasis === 'dividend',
    resolution_basis_reserve: stBasis === 'reserve',
  };
}

/** 병합 payload — 모든 템플릿 공용. */
export function buildOtcVariables(state: OtcState): Record<string, any> {
  const out: Record<string, any> = { ...(state.values || {}) };
  Object.assign(out, expandFlags(state));
  out.allottees = buildAllottees(state);
  out.shares = buildShareClasses(state);
  return out;
}
